import { useCallback, useEffect, useRef, useState } from "react";

import { ErrorMessage, Loading } from "common";

import { Icon } from "@/design-system";

/**
 * The reference's horizontal card rail — its "Order again" section
 * (`<!-- ===== HOME ===== -->` … the `data-hscroll="1"` block; grep
 * `Recently ordered by` in
 * `resources/Dental supply storefront design system/Dens N Dente Tailwind.dc.html`).
 *
 * Four parts, all copied from it: a header row carrying an accent eyebrow, a serif
 * `text-3xl` title and the controls; a scroll-snapped strip of `250px` cards; a 64px
 * gradient fade on the right edge; and the width-animating dot pager.
 *
 * Things worth knowing before changing it:
 *
 * - **It owns its own `max-w-container`,** because the reference's section does. That
 *   means it must not be nested inside another container — the PDP renders it *after*
 *   its main container closes, exactly as the reference PDP puts its related-items rail
 *   in a second container of its own. Nesting it re-creates the double-container bug
 *   (`dnd-frontend-ui/references/design-system.md` → "The page shell").
 * - **The strip's `mx-[-16px]` + `px-[var(--space-4)]` pair is deliberate**: it lets the
 *   first and last card sit flush with the container edge while still giving the cards
 *   room to cast a hover shadow without being clipped. The `mt-[-12px] mb-[-14px]`
 *   cancel the vertical padding that exists for the same reason.
 * - **`scroll-snap-type: x mandatory` with `scroll-padding: 0 16px`** is what makes the
 *   arrows land cards flush rather than mid-card.
 * - The fade's transparent stop is the reference's literal `rgba(246,248,250,0)`, which
 *   is `--surface-page` (`--neutral-50`) at zero alpha. If that token changes, this
 *   needs changing with it — `transparent` is not a safe substitute in every engine.
 */
const ARROW_CLASS =
  "inline-flex h-[38px] w-[38px] shrink-0 cursor-pointer items-center justify-center rounded-full border border-line bg-card text-ink-2 [transition:opacity_var(--duration-fast),background_var(--duration-fast)] enabled:hover:bg-neutral-100 disabled:cursor-default disabled:opacity-40 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

export default function ProductCarousel({
  eyebrow,
  title,
  items = [],
  renderItem,
  loading = false,
  error = null,
  viewAllText,
  onViewAll,
}) {
  const safeItems = Array.isArray(items) ? items : [];
  const stripRef = useRef(null);
  const [rail, setRail] = useState({
    page: 0,
    pages: 1,
    atStart: true,
    atEnd: true,
  });

  const measure = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pages = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
    setRail({
      pages,
      page: max > 0 ? Math.round((el.scrollLeft / max) * (pages - 1)) : 0,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft >= max - 1,
    });
  }, []);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return undefined;

    // Measure after layout rather than during the commit, and again on every later
    // layout change: the arrows and dots are derived from `scrollWidth` / `clientWidth`,
    // and a value read too early would stick — nothing scrolls, so no scroll event would
    // arrive to correct it. The strip catches width changes; its first child catches the
    // cards laying out, which moves `scrollWidth` without changing the strip's own box.
    //
    // Do **not** add a `scrollLeft = 0` reset here. Chrome restores a scroll container's
    // offset when you return to a URL you had scrolled it on, ~35ms after mount —
    // measured: a first visit stays at 0, a revisit lands at `max`. A reset in this
    // effect runs before that and loses, and it should lose: restoring the rail where the
    // user left it is the same behaviour as page-level scroll restoration.
    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure, safeItems.length]);

  const nudge = (direction) => {
    const el = stripRef.current;
    if (el)
      el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const goToPage = (index) => {
    const el = stripRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({
      left: rail.pages > 1 ? (max * index) / (rail.pages - 1) : 0,
      behavior: "smooth",
    });
  };

  const hasRail = !loading && !error && safeItems.length > 0;

  return (
    <section className="mx-auto my-0 max-w-container px-[var(--gutter)] pb-[var(--space-16)] pt-[var(--space-14)]">
      {/* `flex-wrap` is an app-only addition — the reference is a desktop prototype, and
          its controls group is `shrink-0` with a `whitespace-nowrap` "view all", which at
          390px measured 86px past the viewport. Wrapping drops the controls onto their own
          line instead of squashing them, and changes nothing at desktop width. */}
      <div className="mb-[var(--space-7)] flex flex-wrap items-end justify-between gap-[var(--space-4)]">
        <div>
          {eyebrow && (
            <span className="text-xs font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-accent)]">
              {eyebrow}
            </span>
          )}
          <h2 className="mt-[6px] font-sans text-3xl font-semibold tracking-[-.01em] text-ink">
            {title}
          </h2>
        </div>

        {hasRail && (
          <div className="flex shrink-0 items-center gap-[10px]">
            <button
              type="button"
              className={ARROW_CLASS}
              aria-label="Previous"
              disabled={rail.atStart}
              onClick={() => nudge(-1)}
            >
              <Icon name="arrow_back" size={20} weight={400} />
            </button>
            <button
              type="button"
              className={ARROW_CLASS}
              aria-label="Next"
              disabled={rail.atEnd}
              onClick={() => nudge(1)}
            >
              <Icon name="arrow_forward" size={20} weight={400} />
            </button>

            {viewAllText && onViewAll && (
              <button
                type="button"
                onClick={onViewAll}
                className="inline-flex cursor-pointer items-center gap-[6px] whitespace-nowrap border-0 bg-transparent p-0 font-sans text-sm font-semibold text-ink-link no-underline hover:text-blue-700 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
              >
                {viewAllText}
                <Icon name="arrow_forward" size={18} weight={400} />
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <Loading text="Loading..." />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : safeItems.length === 0 ? (
        <p className="text-center text-sm text-ink-3">No items to display</p>
      ) : (
        <>
          <div className="relative mx-[-16px] my-0">
            <div
              ref={stripRef}
              className="dnd-thinscroll mx-0 mb-[-14px] mt-[-12px] flex gap-[var(--space-5)] overflow-x-auto px-[var(--space-4)] pb-[var(--space-4)] pt-[var(--space-3)] [scroll-behavior:smooth] [scroll-padding:0_16px] [scroll-snap-type:x_mandatory]"
            >
              {safeItems.map((item, index) => (
                <div
                  key={item?.id ?? index}
                  className="flex max-w-[250px] flex-[0_0_250px] [scroll-snap-align:start]"
                >
                  {renderItem(item)}
                </div>
              ))}
            </div>

            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 right-[-1px] top-0 w-[64px] [transition:opacity_.2s_var(--ease-standard)]"
              style={{
                background:
                  "linear-gradient(90deg, rgba(246,248,250,0), var(--surface-page))",
                opacity: rail.atEnd ? 0 : 1,
              }}
            />
          </div>

          {rail.pages > 1 && (
            <div className="mt-[22px] flex items-center justify-center gap-[var(--space-2)]">
              {Array.from({ length: rail.pages }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Go to page ${index + 1}`}
                  aria-current={index === rail.page}
                  onClick={() => goToPage(index)}
                  className={`h-[8px] cursor-pointer rounded-[99px] border-0 p-0 [transition:width_.5s_cubic-bezier(.16,1,.3,1)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                    index === rail.page
                      ? "w-[24px] bg-orange-500"
                      : "w-[8px] bg-neutral-300"
                  }`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
